import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { ErrorAlert, Notification, NotificationListResult } from '@xdocs/schemas';
import { useAuth } from '../../auth/AuthProvider.js';
import { useWs } from '../../ws/WsProvider.js';

const PAGE_SIZE = 10;
/** Cheap true-count probe — `pageSize: 1` still returns the real `total`
 * (the paginated envelope's `total` is independent of the page window). */
const UNREAD_COUNT_PROBE_SIZE = 1;

export interface UseNotificationsResult {
  items: readonly Notification[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  unreadOnly: boolean;
  setUnreadOnly: (unreadOnly: boolean) => void;
  hasMore: boolean;
  loadMore: () => void;
  isLoadingMore: boolean;
  loadMoreError: string | null;
  unreadCount: number | null;
  markRead: (notificationId: string | null) => Promise<void>;
  isMarkingRead: boolean;
}

/**
 * Notifications data (F33). List query keyed under `['user', userId,
 * 'notifications', …]` so the generic `user:{userId}` push-invalidation
 * skeleton (`ws/queryInvalidation.ts`) refetches it live on arrival/read-
 * state-sync from another device, with no per-feature channel subscription
 * needed. `markRead` optimistically flips `readAt` locally (E19 mutations:
 * "optimistic UI immediately… on failure revert"), then reconciles with the
 * server via invalidation regardless of outcome.
 */
export function useNotifications(): UseNotificationsResult {
  const { userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected' && userId !== null;

  const [unreadOnly, setUnreadOnlyState] = useState(false);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<Notification[]>([]);

  const listKey = ['user', userId, 'notifications', 'list', { unreadOnly, page }] as const;

  const listQuery = useQuery<NotificationListResult, ErrorAlert>({
    queryKey: listKey,
    queryFn: () =>
      requestTyped(wsClient, 'notification.list', {
        unreadOnly,
        page,
        pageSize: PAGE_SIZE,
        sort: 'createdAt',
        dir: 'desc',
      }),
    enabled,
  });

  useEffect(() => {
    // Only re-runs when this page's data arrives — `react-hooks/exhaustive-deps`
    // isn't registered for plain `.ts` files (see `eslint.config.js`, scoped
    // to `.jsx`/`.tsx`), so no disable directive is needed here.
    if (!listQuery.data) return;
    setItems((prev) => (page === 1 ? listQuery.data.rows : [...prev, ...listQuery.data.rows]));
  }, [listQuery.data]);

  const unreadCountQuery = useQuery<NotificationListResult, ErrorAlert>({
    queryKey: ['user', userId, 'notifications', 'unreadCount'],
    queryFn: () =>
      requestTyped(wsClient, 'notification.list', {
        unreadOnly: true,
        page: 1,
        pageSize: UNREAD_COUNT_PROBE_SIZE,
        sort: 'createdAt',
        dir: 'desc',
      }),
    enabled,
  });

  const setUnreadOnly = useCallback((next: boolean) => {
    setUnreadOnlyState(next);
    setPage(1);
    setItems([]);
  }, []);

  const loadMore = useCallback(() => {
    if (listQuery.isFetching) return;
    setPage((p) => p + 1);
  }, [listQuery.isFetching]);

  const markReadMutation = useMutation<
    { markedCount: number },
    ErrorAlert,
    string | null,
    { previousItems: Notification[]; previousUnread: number | null }
  >({
    mutationFn: (notificationId) => requestTyped(wsClient, 'notification.markRead', { notificationId }),
    onMutate: (notificationId) => {
      const previousItems = items;
      const previousUnread = unreadCountQuery.data?.total ?? null;
      setItems((prev) =>
        prev.map((item) =>
          notificationId === null || item.id === notificationId
            ? { ...item, readAt: item.readAt ?? new Date() }
            : item,
        ),
      );
      return { previousItems, previousUnread };
    },
    onError: (_err, _vars, context) => {
      if (context) setItems(context.previousItems);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['user', userId, 'notifications'] });
    },
  });

  const total = listQuery.data?.total ?? null;
  const hasMore = total !== null ? items.length < total : false;

  return {
    items,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    refetch: () => void listQuery.refetch(),
    unreadOnly,
    setUnreadOnly,
    hasMore,
    loadMore,
    isLoadingMore: page > 1 && listQuery.isFetching,
    loadMoreError: page > 1 && listQuery.isError ? (listQuery.error?.message ?? null) : null,
    unreadCount: unreadCountQuery.data?.total ?? null,
    markRead: (notificationId) => markReadMutation.mutateAsync(notificationId).then(() => undefined),
    isMarkingRead: markReadMutation.isPending,
  };
}
